import { useModalContentToggler } from '@components'

import { Text } from '@billy/design-system'
import round from 'lodash/round'
import { ReactElement, useMemo } from 'react'
import { Trans } from 'react-i18next'

import { SubjectReconciliationModalStateId } from '../../../paymentReconciliationModal/enums/SubjectReconciliationModalStateId'
import { calculateConvertedAmount } from '../../../paymentReconciliationModal/utils'
import { useBankLines, useManualPayment, useMultiplePayableSubjects, usePaymentModal } from '../../contexts'
import { DEFAULT_EXCHANGE_RATE_THRESHOLD } from './constants'
import * as Styled from './styles'
import { calculateExchangeRateDeviation, exchangeRateWarningTranslationKeys } from './utils'

export const ExchangeRateDeviationWarning = (): ReactElement | null => {
  const { activeState } = useModalContentToggler()
  const { crossCurrencyPaymentAmount, manualPaymentAmount } = useManualPayment()
  const { bankLinesSummedAmounts } = useBankLines()
  const { selectedSubjectsAmountsSum } = useMultiplePayableSubjects()
  const { subjectCurrencyId, exchangeRate } = usePaymentModal()

  const targetAmount =
    activeState === SubjectReconciliationModalStateId.ManualPayment ? manualPaymentAmount : bankLinesSummedAmounts
  const deviation = useMemo(
    () =>
      calculateExchangeRateDeviation(
        crossCurrencyPaymentAmount || selectedSubjectsAmountsSum,
        exchangeRate,
        targetAmount,
      ),
    [crossCurrencyPaymentAmount, selectedSubjectsAmountsSum, exchangeRate, targetAmount],
  )
  if (deviation < DEFAULT_EXCHANGE_RATE_THRESHOLD || crossCurrencyPaymentAmount === 0) {
    return null
  }

  const stateKey = activeState === SubjectReconciliationModalStateId.ManualPayment ? 'manualPayment' : 'bankLines'
  const comparatorKey =
    targetAmount > calculateConvertedAmount(exchangeRate, crossCurrencyPaymentAmount) ? 'higher' : 'lower'
  const i18nKey = exchangeRateWarningTranslationKeys[stateKey][comparatorKey]

  return (
    <Styled.WarningSection showIcon={false} variant="warning">
      <Text alignment="center">
        <Trans
          i18nKey={i18nKey}
          values={{
            currencyId: subjectCurrencyId,
            deviation: round(deviation),
            subjectAmount: crossCurrencyPaymentAmount,
          }}
          components={{ strong: <strong /> }}
        />
      </Text>
    </Styled.WarningSection>
  )
}
